import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  loadPipeline,
  refreshPipeline,
  publish,
  deletePipeline as deletePipelineById,
  updatePipelineInfo,
  pausePipeline as pausePipelineById,
} from 'js/actions';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

type RefreshOptions = { addToRoot?: boolean; ignoreErrors?: boolean };

export default function usePipelinesAdapter() {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  // Stable across renders — several callers (e.g. ByoCreateCanvas) put these
  // in a useEffect dependency array. An unstable reference there re-fires the
  // effect every render the dispatch causes, which re-fires the effect again:
  // an infinite fetch loop. useCallback([dispatch]) is enough since dispatch
  // itself is stable.
  const refreshPipelineById = useCallback(
    (id: number, options?: RefreshOptions) => dispatch(refreshPipeline(id, options)),
    [dispatch],
  );

  const getPipelineById = useCallback(
    async (id: number, options?: RefreshOptions) => {
      const pipeline = await refreshPipelineById(id, options);
      return pipeline?.entities?.flowNodes?.[id];
    },
    [refreshPipelineById],
  );

  const pausePipeline = useCallback(
    (flowNodeId: number) => dispatch(pausePipelineById(flowNodeId)),
    [dispatch],
  );

  const requestPipelineById = useCallback(
    ({ id, ignoreErrors, isPreview }) => dispatch(loadPipeline(id, { isPreview, ignoreErrors })),
    [dispatch],
  );

  const publishPipeline = useCallback((flowId: number) => dispatch(publish(flowId)), [dispatch]);

  const updatePipeline = useCallback(
    (flow: { [key: string]: any }, info: any) => dispatch(updatePipelineInfo(flow, info)),
    [dispatch],
  );

  const deletePipeline = useCallback(
    (flowNodeId: number) => dispatch(deletePipelineById(flowNodeId)),
    [dispatch],
  );

  return {
    getPipelineById,
    pausePipeline,
    deletePipeline,
    requestPipelineById,
    refreshPipelineById,
    publishPipeline,
    updatePipeline,
  };
}
